/**
 * Prosty dobór od zera (`/dobierz-zestaw`): cele zamiast kilowatów (makieta „VoltValue dla domu” wersja 12, D2,
 * decyzje właściciela 2026-10-24) i krok „Sprzęt dobierzemy za Ciebie” (D12).
 *
 * Czyste funkcje, bez Reacta: z odpowiedzi kreatora (rachunek, cele, budżet, opcjonalnie fazy i miejsce modułów)
 * składamy żądanie do silnika doboru (`POST /api/v1/kits/suggest`, schemat `backend_api/kits/schemas.py`
 * `SuggestRequest`). Teksty dla użytkownika są w słowniku `asystent` (`prosty.cele…`), tutaj tylko klucze.
 *
 * ═══ CEL → DOBÓR (prawdziwe pola żądania; czego dane nie dźwigają, mówimy w wyniku, nie udajemy) ═══
 *
 * | Cel                     | Co zmienia w żądaniu                                                    | Czego nie umiemy   |
 * |-------------------------|--------------------------------------------------------------------------|--------------------|
 * | rachunek (niższy)       | `panels.target_power_kwp` z rocznego zużycia (`mocZZuzycia`, ta sama     | -                  |
 * |                         | reguła co kalkulator mocy); kolejność wariantów silnika, najtańszy 1.    |                    |
 * | awaria (prąd przy       | magazyn obowiązkowy (`categories` + `battery`), falownik `types_in:      | silnik nie zna     |
 * |   awarii)               | ['Hybrydowy']` jako `required`, większy zapas magazynu (75% dobowego     | wyjścia awaryjnego |
 * |                         | zużycia zamiast 55%)                                                     | (EPS): wykonawca   |
 * | auto (ładowanie auta)   | +2 600 kWh rocznie (`DODATKI.auto`, kalkulator mocy) do zużycia, chyba   | ładowarki nie ma   |
 * |                         | że już jest w rachunku; pytanie o fazy                                   | w zestawie         |
 * | pompa (pompa ciepła)    | +4 500 kWh rocznie (`DODATKI.pompa`), pytanie o fazy                     | brak profilu       |
 * |                         |                                                                          | zimowego zużycia   |
 * | estetyka                | moduły `types_in: ['Full Black']` (`required`); na gruncie zostaje       | -                  |
 * |                         | dwustronny, a czarna rama `frame_colour` jako `preferred`; gdy nic nie   |                    |
 * |                         | wchodzi, drugi przebieg z `preferred` i uczciwa notka                    |                    |
 * | jakosc (najwyższa)      | wariant silnika `quality` (półki marek `kits/polki_marek.py`, sprawność, | rankingu nie       |
 * |                         | gwarancje, dane badawcze marek) pokazany jako pierwszy                   | wymyślamy          |
 * | dotacja (PME2, Polska)  | magazyn obowiązkowy, `capacity_kwh_min` = max(10, 2 × kWp),              | EMS i praca        |
 * |                         | `battery.filters.max_price_per_kwh` = 3 000 zł (limit programu),         | wyspowa: wykonawca |
 * |                         | łączenie modułów, falownik hybrydowy; szacunek dotacji z `lib/pme2`      |                    |
 * | szybki_montaz           | `constraints.require_available` (tylko oferty oznaczone jako dostępne,  | terminu montażu    |
 * |                         | i tak domyślne), w zleceniu termin „Jak najszybciej” (`szybko`)          | nie znamy          |
 *
 * Magazyn: cele „awaria” i „dotacja” (albo `?magazyn=tak` ze strony głównej) dają tylko
 * zestawy z magazynem; `?magazyn=nie` bez magazynu; inaczej dwie grupy (z magazynem i bez), żeby było widać różnicę.
 * Rozbudowa magazynu (instalacja PV już jest) to osobna ścieżka, nie cel: patrz `jestRozbudowa` i `trybRozbudowy`.
 */
import { mocZZuzycia, rocznyKwh } from './klienci/energia'
import { KONSTRUKCJA_DOMYSLNA, kosztKonstrukcji } from './konstrukcja'
import { kosztMontazu } from '../config/cennikMontazu'
import { KWH_NA_KWP, MAKS_CENA_ZA_KWH, MIN_POJEMNOSC_KWH } from '../config/pme2'
import { DODATKI } from './mocKalkulator'
import { jestPolska } from '../kraj'
import { ogloszenieZZestawu } from './rynek'
import { opisSprzetuKlienta } from './sprzetKlienta'

/** Cele w kolejności z makiety. `tylkoPolska`: dotacja to program NFOŚiGW. */
export const CELE = [
  { klucz: 'rachunek' },
  // Dziewiąty cel do siatki 3×3 (runda 9, 2026-10-26): najniższa cena za kWp, czyli za każdą wyprodukowaną kWh.
  { klucz: 'zwrot' },
  { klucz: 'awaria' },
  { klucz: 'auto' },
  { klucz: 'pompa' },
  { klucz: 'estetyka' },
  { klucz: 'jakosc' },
  { klucz: 'dotacja', tylkoPolska: true },
  { klucz: 'szybki_montaz' },
]

export const celeKraju = (polska = jestPolska) => CELE.filter((c) => polska || !c.tylkoPolska)

const ZNANE_CELE = new Set(CELE.map((c) => c.klucz))
const CELE_Z_MAGAZYNEM = ['awaria', 'dotacja']

/**
 * Rozbudowa to osobna ścieżka (decyzje właściciela 2026-10-24 i 2026-10-26, second-brain/05): pierwsze pytanie „Masz
 * już instalację PV?” (`masz_pv`). Przy „tak” celów nie ma; klient zaznacza części do rozbudowy (`czesci`: moduły,
 * falownik, magazyn energii, kilka naraz) i dalsze pytania oraz wynik dotyczą tylko ich.
 */
export const jestRozbudowa = (o) => o?.masz_pv === 'tak'

export const CZESCI_ROZBUDOWY = ['moduly', 'falownik', 'magazyn']
// Nazwy części w opisie zlecenia (widzą je wykonawcy, Rynek jest po polsku).
const NAZWY_CZESCI = { moduly: 'moduły', falownik: 'falownik', magazyn: 'magazyn energii' }

/**
 * Zaznaczone części rozbudowy. Odpowiedzi sprzed 2026-10-26 (bez `czesci`) dotyczyły samego magazynu, więc tak je
 * czytamy, żeby wznowiony dobór i zapisany wybór nie zmieniły znaczenia.
 */
export const czesciRozbudowy = (o) => {
  if (!jestRozbudowa(o)) return new Set()
  if (!Array.isArray(o?.czesci)) return new Set(['magazyn'])
  return new Set(o.czesci.filter((c) => CZESCI_ROZBUDOWY.includes(c)))
}

/** Zbiór wybranych celów (tylko znane; dotacja tylko w Polsce; przy rozbudowie pusty). */
export const wybraneCele = (o, polska = jestPolska) => new Set(
  (Array.isArray(o?.cele) && !jestRozbudowa(o) ? o.cele : [])
    .filter((c) => ZNANE_CELE.has(c))
    .filter((c) => polska || c !== 'dotacja'),
)

/**
 * „Sprzęt dobierzemy za Ciebie” (D12) i pola zlecenia z kontraktu (`zadania/2026-10_plan-rynek-dla-domu-produkcja.md`
 * sekcja 4): `kto_dobiera` (voltvalue | wykonawca), `kto_kupuje` (klient | wykonawca). Para „wykonawca dobiera,
 * klient kupuje” jest zabroniona, więc jej tu nie ma. `sprzet` to dzisiejsze pole `Ogloszenie.sprzet`
 * (`z_firma`, `kupie_sam`), dopóki P0 nie zamieni go na dwa pola.
 */
export const OPCJE_SPRZETU = [
  { v: 'wykonawca', kto_dobiera: 'wykonawca', kto_kupuje: 'wykonawca', sprzet: 'z_firma' },
  { v: 'zestaw_wykonawca', kto_dobiera: 'voltvalue', kto_kupuje: 'wykonawca', sprzet: 'z_firma' },
  { v: 'zestaw_klient', kto_dobiera: 'voltvalue', kto_kupuje: 'klient', sprzet: 'kupie_sam' },
]

export const opcjaSprzetu = (v) => OPCJE_SPRZETU.find((x) => x.v === v) || null
export const opcjaZKto = (ktoDobiera, ktoKupuje) =>
  OPCJE_SPRZETU.find((x) => x.kto_dobiera === ktoDobiera && x.kto_kupuje === ktoKupuje) || null

/**
 * „Kto wybiera sprzęt?” (drugie pytanie, decyzja 2026-10-26): `kto_wybiera` = `wykonawca` („Wykonawca zaproponuje
 * sprzęt w ofercie”) albo `voltvalue` („Wybiorę zestaw z propozycji VoltValue”, wtedy `kto_kupuje` = `wykonawca`
 * albo `klient`). Zamiana na opcję z `OPCJE_SPRZETU`; `sprzet` z adresu albo ze starszych odpowiedzi nadal działa.
 */
export const opcjaZOdpowiedzi = (o) => {
  // Pominięte pytanie (null, droga „Kilka pytań”, 2026-10-26) zostawia decyzję wykonawcy.
  if (o?.kto_wybiera === 'wykonawca' || o?.kto_wybiera === null) return opcjaSprzetu('wykonawca')
  // „Mam już wybrany sprzęt” kupuje się jak konkretny sprzęt (`config/rynek.SPRZET_KONKRETNY`): te same pola co zestaw.
  if (o?.kto_wybiera === 'voltvalue' || o?.kto_wybiera === 'wlasny') {
    return opcjaZKto('voltvalue', o?.kto_kupuje === 'klient' ? 'klient' : 'wykonawca')
  }
  return opcjaSprzetu(o?.sprzet)
}

/** „Mam już wybrany sprzęt” (decyzja 2026-10-26): karty sprzętu zamiast zestawów, sprzęt idzie do opisu zlecenia. */
export const maWlasnySprzet = (o) => o?.kto_wybiera === 'wlasny'

/** „Wykonawca zaproponuje sprzęt”: wynik to podsumowanie zlecenia, bez zestawów ze sklepów. */
export const wykonawcaProponuje = (o) => opcjaZOdpowiedzi(o)?.kto_dobiera === 'wykonawca'

/** Czy dobieramy zestaw: nie przy „wszystko po stronie wykonawcy” i nie przy sprzęcie wybranym przez klienta. */
export const dobieramyZestaw = (o) => !wykonawcaProponuje(o) && !maWlasnySprzet(o)

/* ═══ Zużycie i moc ═══════════════════════════════════════════════════════════════════════════ */

/**
 * Planowane odbiorniki z celów („Zasilanie planowanej pompy ciepła”, „Planowane ładowanie auta”, decyzja właściciela
 * 2026-10-26, runda 9): zawsze doliczamy ich zużycie, bez dopytania. Ile kWh rocznie: `DODATKI` w `lib/mocKalkulator`
 * (pompa 4 500 kWh, auto 2 600 kWh), te same wartości co w kalkulatorze mocy.
 */
export const dodatkiZCelow = (o) => {
  const cele = wybraneCele(o)
  return ['auto', 'pompa'].filter((k) => cele.has(k))
}

/** Odpowiedź o zużycie jako profil energetyczny (`lib/klienci/energia.js`) razem z odbiornikami z celów. */
export const energiaZOdpowiedzi = (o) => {
  const z = o?.zuzycie
  return {
    zuzycie: z?.jednostka === 'zl'
      ? { sposob: 'rachunek', rachunekZl: z.ile, dodatkiWliczone: o?.dodatki_w_rachunku === 'jest' }
      : { sposob: 'kwh', kwh: z?.ile, dodatkiWliczone: o?.dodatki_w_rachunku === 'jest' },
    dodatki: dodatkiZCelow(o),
  }
}

/** Roczne zużycie w kWh razem z planowanymi odbiornikami albo null. */
export const roczneZuzycie = (zuzycieLubOdpowiedzi) => {
  // Zgodność wstecz: dawniej argumentem było samo `zuzycie` ({ ile, jednostka }).
  const o = zuzycieLubOdpowiedzi && 'ile' in zuzycieLubOdpowiedzi
    ? { zuzycie: zuzycieLubOdpowiedzi } : zuzycieLubOdpowiedzi
  if (!o?.zuzycie?.ile) return null
  return rocznyKwh(energiaZOdpowiedzi(o)) || null
}

/** Moc instalacji w kWp z odpowiedzi (ta sama reguła co kalkulator mocy) albo null. */
export const mocZOdpowiedzi = (o, zMagazynem) => {
  if (!roczneZuzycie(o)) return null
  return mocZZuzycia(energiaZOdpowiedzi(o), { magazyn: zMagazynem, montaz: o.montaz === 'grunt' ? 'grunt' : 'dach' }).mocKwp
}

/**
 * Pytanie o fazy zadajemy tylko wtedy, gdy ma znaczenie: przy aucie i pompie ciepła (zwykle trzy fazy) albo przy
 * instalacji powyżej 6 kWp (falowniki jednofazowe kończą się zwykle na 6 kW). Inaczej silnik dostaje `null`
 * i pokazuje falowniki z obu grup, jak przy „Nie wiem”.
 */
export const PROG_FAZ_KWP = 6
export const potrzebaFaz = (o) => {
  const cele = wybraneCele(o)
  if (cele.has('auto') || cele.has('pompa')) return true
  const kwp = mocZOdpowiedzi(o, false)
  return Boolean(kwp && kwp > PROG_FAZ_KWP)
}

/* ═══ Magazyn ═══════════════════════════════════════════════════════════════════════════════════ */

/** Czy cele same przesądzają o magazynie (wtedy nie pytamy „Chcesz magazyn energii?”). */
export const celeWymagajaMagazynu = (o) => CELE_Z_MAGAZYNEM.some((c) => wybraneCele(o).has(c))

// Pytanie o magazyn pada zawsze (runda 9): jawne „Nie” wygrywa z celem, który magazyn tylko wstępnie zaznacza.
export const wymagaMagazynu = (o) => (jestRozbudowa(o) ? czesciRozbudowy(o).has('magazyn')
  : o?.magazyn === 'tak' || (o?.magazyn !== 'nie' && celeWymagajaMagazynu(o)))

/** Plan zapytań: [true] tylko z magazynem, [false] tylko bez, [true, false] obie grupy. */
export const planMagazynu = (o) => {
  if (wymagaMagazynu(o)) return [true]
  if (o?.magazyn === 'nie') return [false]
  return [true, false]
}

// Ile dobowego zużycia ma pokryć magazyn: przy „prądzie przy awarii” bateria bywa jedynym źródłem, więc większy zapas
// (wcześniej odpowiedź „często” na pytanie o zaniki).
const UDZIAL_AWARIA = 0.75
const UDZIAL_DOMYSLNY = 0.55
const doPolowek = (v) => Math.round(v * 2) / 2
const wGore = (v) => Math.ceil(v * 2) / 2

/** Najmniejsza pojemność do PME2: 10 kWh i 2 kWh na każdy kWp (zasada 2:1, `config/pme2.js`). */
export const minPojemnoscPme2 = (kwp) => Math.max(MIN_POJEMNOSC_KWH, kwp ? wGore(kwp * KWH_NA_KWP) : 0)

/** Zakres pojemności magazynu dla silnika: { min, max, pojemnosc } albo null bez zużycia. */
export const zakresMagazynu = (o, rok, kwp) => {
  if (!rok) return null
  const cele = wybraneCele(o)
  const udzial = cele.has('awaria') ? UDZIAL_AWARIA : UDZIAL_DOMYSLNY
  let pojemnosc = doPolowek((rok / 365) * udzial)
  let min = Math.max(2, pojemnosc - 2)
  if (cele.has('dotacja')) {
    const pme2 = minPojemnoscPme2(kwp)
    pojemnosc = Math.max(pojemnosc, pme2)
    min = pme2
  }
  return { min, max: pojemnosc + 3, pojemnosc }
}

/* ═══ Koszty i żądanie ═════════════════════════════════════════════════════════════════════════ */

/**
 * Szacunek konstrukcji i montażu dla mocy instalacji, jak w pełnym asystencie (`kosztyWlasne` w KitAssistant):
 * konstrukcja z cennika dla gruntu albo domyślnego dachu, montaż z `config/cennikMontazu`.
 */
export const kosztyKonstrukcjiIMontazu = (kwp, montaz) => {
  if (!kwp) return 0
  const k = kosztKonstrukcji({ kwp, konstrukcja: montaz === 'grunt' ? 'grunt' : KONSTRUKCJA_DOMYSLNA })
  return (k?.razemZl || 0) + (kosztMontazu(kwp) || 0)
}

/**
 * Odpowiedzi kreatora na żądanie do silnika doboru.
 *
 * `zMagazynem` jest PARAMETREM, bo przy dwóch grupach budujemy dwa żądania. `luzujEstetyke` to drugi przebieg, gdy
 * modułów full black nie dało się dopasować: wtedy są tylko preferencją, a wynik mówi o tym wprost.
 *
 * Budżet znaczy całą instalację z konstrukcją i montażem (jak w pełnym asystencie i na stronie głównej, 2026-10-23).
 * Silnik liczy sam sprzęt z dostawą, więc szacunek konstrukcji i montażu odejmujemy przed wysłaniem.
 */
/**
 * Magazyn w rozbudowie (decyzje właściciela 2026-10-24 do 2026-10-26, second-brain/05). Kreator dopytuje „Masz już
 * magazyn?” (`masz_magazyn`):
 * - `tak`: powiększenie istniejącego magazynu o moduły tej samej marki (`marka_magazynu`, gdy podana);
 * - `nie`: nowy magazyn zgodny z falownikiem: z wymianą falownika na hybrydowy, bez wymiany, gdy obecny falownik jest
 *   hybrydowy (`falownik_hybrydowy` = `tak`); przy zaznaczonej części „falownik” zawsze z nowym falownikiem.
 * Null, gdy magazyn nie jest zaznaczony albo nie wiadomo jeszcze, czy jest.
 */
export const trybRozbudowy = (o) => {
  const czesci = czesciRozbudowy(o)
  if (!czesci.has('magazyn')) return null
  if (o?.masz_magazyn === 'tak') return 'dokladka'
  if (czesci.has('falownik')) return 'z_falownikiem'
  if (o?.masz_magazyn === 'nie') return o?.falownik_hybrydowy === 'tak' ? 'sam_magazyn' : 'z_falownikiem'
  return null
}

/** Dokładka modułów wybrana wprost („O ile chcesz rozbudować?”, runda 6 2026-10-26): +2, +4, +6 kWp albo własna. */
export const WYBORY_DOKLADKI = ['2', '4', '6']
export const wybranaDokladka = (o) => {
  if (WYBORY_DOKLADKI.includes(o?.dokladka)) return Number(o.dokladka)
  if (o?.dokladka === 'wlasna' && Number(o?.dokladka_kwp) > 0) return Math.round(Number(o.dokladka_kwp) * 10) / 10
  return null
}

/**
 * Moc modułów do dołożenia: wybrana wprost, a przy „Nie wiem, dobierzcie do zużycia” moc z zużycia (ta sama reguła
 * co przy nowej instalacji) minus obecna moc, co najmniej 2 kWp, bo mniejszej dokładki nikt nie montuje. Bez obecnej
 * mocy połowa mocy z zużycia (uczciwy szacunek, wynik mówi, że to przybliżenie). Null bez zużycia.
 */
export const mocDokladki = (o) => {
  const wybrana = wybranaDokladka(o)
  if (wybrana) return wybrana
  const potrzebna = mocZOdpowiedzi(o, czesciRozbudowy(o).has('magazyn'))
  if (!potrzebna) return null
  const obecna = Number(o?.obecna_moc) || 0
  const dokladka = obecna > 0 ? potrzebna - obecna : potrzebna / 2
  return Math.round(Math.max(2, dokladka) * 10) / 10
}

/** Czy w rozbudowie dobieramy nowy falownik: zaznaczona wymiana albo nowy magazyn bez hybrydowego falownika. */
export const nowyFalownikRozbudowy = (o) => {
  const czesci = czesciRozbudowy(o)
  const tryb = trybRozbudowy(o)
  return czesci.has('falownik') || (czesci.has('magazyn') && tryb !== 'dokladka' && tryb !== 'sam_magazyn')
}

/** Żądanie do silnika przy rozbudowie: tylko zaznaczone części, falownik do łącznej mocy po rozbudowie. */
const zapytanieRozbudowy = (o) => {
  const czesci = czesciRozbudowy(o)
  const tryb = trybRozbudowy(o)
  const rok = roczneZuzycie(o)
  const dokladka = czesci.has('moduly') ? mocDokladki(o) : null
  const obecna = Number(o?.obecna_moc) || 0
  // Moc, do której dobieramy falownik i magazyn: obecna z dokładką; bez obecnej mocy z zużycia, nie mniej niż dokładka.
  const zZuzycia = rok ? mocZOdpowiedzi(o, true) : null
  const kwp = obecna ? obecna + (dokladka || 0) : Math.max(zZuzycia || 0, dokladka || 0) || null
  const nowyFalownik = nowyFalownikRozbudowy(o)
  const hybrydowy = czesci.has('magazyn') || o?.nowy_falownik === 'hybrydowy'
  const zakres = czesci.has('magazyn') ? zakresMagazynu(o, rok, kwp) : null
  const marka = tryb === 'dokladka' && o?.marka_magazynu && o.marka_magazynu !== 'inna' ? o.marka_magazynu : null
  const koszty = dokladka ? kosztyKonstrukcjiIMontazu(dokladka, 'dach') : 0
  const zapytanie = {
    categories: [
      ...(czesci.has('moduly') ? ['panels'] : []),
      ...(nowyFalownik ? ['inverter'] : []),
      ...(czesci.has('magazyn') ? ['battery'] : []),
    ],
    budget_total_pln: o.budzet ? Math.max(0, o.budzet - koszty) : null,
    // Fazy pytane przy każdym nowym falowniku (runda 6: bez pytania silnik dał najtańszy jednofazowy).
    installation_phase: o.fazy === '1' || o.fazy === '3' ? o.fazy : null,
    panels: { target_power_kwp: dokladka, filters: {}, modes: {} },
    inverter: {
      power_kw_min: kwp ? Math.max(1, Math.floor(kwp * 0.8)) : null,
      power_kw_max: kwp ? Math.ceil(kwp) : null,
      filters: { ...(hybrydowy ? { types_in: ['Hybrydowy'] } : {}), ...filtrFaz(o) },
      modes: hybrydowy ? { types_in: 'required' } : {},
    },
    battery: zakres
      ? {
        capacity_kwh_min: zakres.min, capacity_kwh_max: zakres.max, laczenie: true,
        filters: marka ? { manufacturer_in: [marka] } : {}, modes: marka ? { manufacturer_in: 'required' } : {},
      }
      : { filters: {}, modes: {} },
    constraints: { require_available: true, allow_unknown_compatibility: false, single_seller_only: false },
  }
  return { zapytanie, kwp: dokladka, pojemnosc: zakres ? zakres.pojemnosc : null, rok: rok ? Math.round(rok) : null, koszty }
}

/**
 * Fazy falownika dla silnika. Silnik NIE czyta `installation_phase` (pole jest w schemacie, ale żaden etap go nie
 * używa); fazy odsiewa wyłącznie `inverter.filters.phase_in` (`kits/candidates.py`). Błąd z produkcji 2026-10-26:
 * przy „3 fazy” dobór dał jednofazowy DEYE SG05LP1, bo filtra nie było.
 */
export const filtrFaz = (o) => (o?.fazy === '1' || o?.fazy === '3' ? { phase_in: [`${o.fazy} Fazowy`] } : {})

export function zbudujZapytanie(o, zMagazynem = wymagaMagazynu(o), { luzujEstetyke = false } = {}) {
  if (jestRozbudowa(o)) return zapytanieRozbudowy(o)
  const cele = wybraneCele(o)
  const rok = roczneZuzycie(o)
  const kwp = rok ? mocZOdpowiedzi(o, zMagazynem) : null
  const zakres = zMagazynem ? zakresMagazynu(o, rok, kwp) : null
  const koszty = kosztyKonstrukcjiIMontazu(kwp, o.montaz)
  const grunt = o.montaz === 'grunt'

  // Moduły: na gruncie dwustronne jako preferencja (silnik punktuje, nie odrzuca; decyzja 2026-10-26, wcześniej
  // wymóg); estetyka to full black, a na gruncie czarna rama jako preferencja. „Zdecyduję później” bez filtra montażu.
  const filtryModulow = {}
  const trybyModulow = {}
  if (grunt) {
    filtryModulow.types_in = ['Bifacial']
    trybyModulow.types_in = 'preferred'
    if (cele.has('estetyka')) {
      filtryModulow.frame_colour = 'Czarna rama'
      trybyModulow.frame_colour = 'preferred'
    }
  } else if (cele.has('estetyka')) {
    filtryModulow.types_in = ['Full Black']
    trybyModulow.types_in = luzujEstetyke ? 'preferred' : 'required'
  }

  const filtryMagazynu = cele.has('dotacja') ? { max_price_per_kwh: MAKS_CENA_ZA_KWH } : {}
  const laczenie = cele.has('dotacja')

  const zapytanie = {
    categories: ['panels', 'inverter', ...(zMagazynem ? ['battery'] : [])],
    budget_total_pln: o.budzet ? Math.max(0, o.budzet - koszty) : null,
    installation_phase: o.fazy === '1' || o.fazy === '3' ? o.fazy : null,
    panels: {
      target_power_kwp: kwp,
      filters: filtryModulow,
      modes: trybyModulow,
    },
    inverter: {
      // Zakres mocy falownika z mocy instalacji, ta sama reguła co w asystencie pełnym: falownik mniejszy lub
      // równy mocy modułów, w pełnych kilowatach.
      power_kw_min: kwp ? Math.max(1, Math.floor(kwp * 0.8)) : null,
      power_kw_max: kwp ? Math.ceil(kwp) : null,
      filters: { ...(zMagazynem ? { types_in: ['Hybrydowy'] } : {}), ...filtrFaz(o) },
      modes: zMagazynem ? { types_in: 'required' } : {},
    },
    battery: zMagazynem && zakres
      ? { capacity_kwh_min: zakres.min, capacity_kwh_max: zakres.max, laczenie, filters: filtryMagazynu, modes: {} }
      : { filters: {}, modes: {} },
    // „Szybki montaż” nie dokłada tu nic nowego: dostępność w sklepie jest wymagana zawsze.
    constraints: { require_available: true, allow_unknown_compatibility: false, single_seller_only: false },
  }
  return { zapytanie, kwp, pojemnosc: zakres ? zakres.pojemnosc : null, rok: rok ? Math.round(rok) : null, koszty }
}

/** Czy przy braku wyników warto spróbować drugi raz z full black jako preferencją. */
export const moznaLuzowacEstetyke = (o) => wybraneCele(o).has('estetyka') && o?.montaz !== 'grunt'

/* ═══ Wynik ═════════════════════════════════════════════════════════════════════════════════════ */

/**
 * Kolejność kart: przy „Najwyższej jakości” pierwszy jest wariant silnika `quality`, inaczej kolejność silnika
 * (najtańszy pierwszy, `VARIANT_ORDER` w `kits/engine.py`).
 */
export const uporzadkujWarianty = (warianty, o) => {
  let lista = [...(warianty || [])]
  const cele = wybraneCele(o)
  // „Szybki zwrot inwestycji”: najpierw najniższa cena za kWp. Silnik nie zna produkcji, ale przy tym samym dachu
  // roczna produkcja rośnie z mocą, więc cena za kWp to cena za każdą wyprodukowaną kWh.
  if (cele.has('zwrot')) {
    lista = lista.map((v, i) => ({ v, i, c: cenaZaKwp(v) }))
      .sort((a, b) => (a.c ?? Infinity) - (b.c ?? Infinity) || a.i - b.i).map((x) => x.v)
  }
  if (!cele.has('jakosc')) return lista
  const i = lista.findIndex((v) => v.variant === 'quality')
  if (i > 0) lista.unshift(lista.splice(i, 1)[0])
  return lista
}

/** Cena zestawu za kWp mocy modułów albo null (bez modułów albo bez ceny). */
export const cenaZaKwp = (v) => {
  const kwp = v?.summary?.panels_total_kwp
  const cena = v?.totals?.total_pln
  return kwp > 0 && cena > 0 ? cena / kwp : null
}

/** Najniższa kwota całego zestawu z wariantów (sprzęt z dostawą) albo null. */
export const najnizszaCena = (warianty) => {
  const kwoty = (warianty || []).map((v) => v?.totals?.total_pln).filter((x) => Number.isFinite(x) && x > 0)
  return kwoty.length ? Math.min(...kwoty) : null
}

/** Łączna pojemność magazynu i cena pozycji magazynu w wariancie (do szacunku dotacji). */
export const magazynWariantu = (v) => {
  const poz = (v?.items || []).find((p) => p.category === 'battery')
  if (!poz) return null
  const s = v.summary || {}
  const kwh = s.battery_capacity_total_kwh
    ?? (s.battery_capacity_kwh ? s.battery_capacity_kwh * (s.battery_count || poz.quantity || 1) : null)
  return { kwh, cena: poz.line_price_pln ?? (poz.unit_price_pln ? poz.unit_price_pln * (poz.quantity || 1) : null) }
}

/**
 * Co cele zmieniły w doborze: lista { klucz, ton, tekst, wartosci } do pokazania pod wynikiem. `tekst` to klucz
 * w słowniku `asystent` (`prosty.cele.skutek.*`). `ton`: `ok` (zrobione), `uwaga` (zrobione częściowo albo
 * do potwierdzenia przez wykonawcę).
 */
export const skutkiCelow = (o, { rok, kwp, luzowanaEstetyka = false } = {}) => {
  const cele = wybraneCele(o)
  const w = o?.dodatki_w_rachunku === 'jest'
  const kwh = (klucz) => DODATKI.find((d) => d.key === klucz)?.kwh
  const out = []
  const dodaj = (klucz, ton, tekst, wartosci = {}) => out.push({ klucz, ton, tekst, wartosci })
  if (cele.has('rachunek')) dodaj('rachunek', 'ok', 'rachunek', { rok })
  if (cele.has('zwrot')) dodaj('zwrot', 'ok', 'zwrot')
  if (cele.has('awaria')) dodaj('awaria', 'uwaga', 'awaria')
  if (cele.has('auto')) dodaj('auto', 'uwaga', w ? 'auto_w_rachunku' : 'auto', { kwh: kwh('auto') })
  if (cele.has('pompa')) dodaj('pompa', 'uwaga', w ? 'pompa_w_rachunku' : 'pompa', { kwh: kwh('pompa') })
  const czesci = czesciRozbudowy(o)
  if (czesci.has('moduly')) {
    const tekst = wybranaDokladka(o) ? 'rozbudowa_moduly_wybrana' : Number(o?.obecna_moc) > 0 ? 'rozbudowa_moduly' : 'rozbudowa_moduly_szacunek'
    dodaj('rozbudowa_moduly', wybranaDokladka(o) ? 'ok' : 'uwaga', tekst, { kwp: String(kwp || '').replace('.', ',') })
  }
  if (czesci.has('falownik')) dodaj('rozbudowa_falownik', 'uwaga', o?.nowy_falownik === 'hybrydowy' || czesci.has('magazyn') ? 'rozbudowa_falownik_hybrydowy' : 'rozbudowa_falownik')
  if (czesci.has('magazyn')) {
    const tryb = trybRozbudowy(o)
    dodaj('rozbudowa_magazynu', 'uwaga', tryb ? `rozbudowa_${tryb}` : 'rozbudowa_magazynu')
  }
  if (cele.has('estetyka')) {
    if (o?.montaz === 'grunt') dodaj('estetyka', 'uwaga', 'estetyka_grunt')
    else dodaj('estetyka', luzowanaEstetyka ? 'uwaga' : 'ok', luzowanaEstetyka ? 'estetyka_luzowana' : 'estetyka')
  }
  if (cele.has('jakosc')) dodaj('jakosc', 'ok', 'jakosc')
  if (cele.has('dotacja')) dodaj('dotacja', 'uwaga', 'dotacja', { min: String(minPojemnoscPme2(kwp)).replace('.', ',') })
  if (cele.has('szybki_montaz')) dodaj('szybki_montaz', 'uwaga', 'szybki_montaz')
  return out
}

/* ═══ Adres, zapis wyboru i zlecenie ═══════════════════════════════════════════════════════════ */

// Kwota w pytaniu o budżet: Polska bez zmian, inne kraje przeliczone kursem orientacyjnym (5 000 zł -> 6 000 lei).
export const BUDZET_MIN = jestPolska ? 5000 : 6000

/**
 * Odpowiedzi podane wcześniej, z adresu: strona główna (`?magazyn=tak|nie&budzet=`, 2026-10-23) i strona
 * „VoltValue dla domu” (P3): `?rachunek=350&cele=rachunek,awaria&kto_dobiera=voltvalue&kto_kupuje=klient`
 * (albo `kwh=` zamiast rachunku). Nieznane wartości pomijamy.
 */
export const odpowiedziZAdresu = (search) => {
  const q = new URLSearchParams(search)
  const o = {}
  if (['tak', 'nie', 'nie_wiem'].includes(q.get('magazyn'))) o.magazyn = q.get('magazyn')
  const budzet = Number(q.get('budzet'))
  if (Number.isFinite(budzet) && budzet >= BUDZET_MIN) o.budzet = Math.round(budzet)
  const rachunek = Number(q.get('rachunek'))
  const kwh = Number(q.get('kwh'))
  if (Number.isFinite(rachunek) && rachunek > 0) o.zuzycie = { ile: Math.round(rachunek), jednostka: 'zl' }
  else if (Number.isFinite(kwh) && kwh > 0) o.zuzycie = { ile: Math.round(kwh), jednostka: 'kwh' }
  if (q.get('cele')) {
    const cele = q.get('cele').split(',').map((c) => c.trim()).filter((c) => ZNANE_CELE.has(c))
    if (cele.length) o.cele = [...new Set(cele)]
  }
  const opcja = opcjaZKto(q.get('kto_dobiera'), q.get('kto_kupuje'))
  if (opcja) {
    o.kto_wybiera = opcja.kto_dobiera
    if (opcja.kto_dobiera === 'voltvalue') o.kto_kupuje = opcja.kto_kupuje
  }
  return o
}

const KLUCZ_WYBORU = 'vv:dobor-dla-domu'
// Jak szkic zlecenia (`lib/rynek.js`): przeżywa rejestrację i link z maila otwarty w nowej karcie.
const WAZNOSC_WYBORU_MS = 3 * 24 * 60 * 60 * 1000

/**
 * Wybór z prostego doboru dla formularza zlecenia (P3/P0), dopóki ten nie przyjmie go wprost: `localStorage`
 * `vv:dobor-dla-domu` = `{ dane, czas }`, ważne 3 dni. `dane`: `cele`, `kto_dobiera`, `kto_kupuje`, `sprzet`
 * (dzisiejsze pole zlecenia), `termin` (`szybko` przy „Szybkim montażu”), `pme2`, `budzet_kwota`, `zuzycie_kwh`,
 * `kwp`, `magazyn_kwh`, `fazy`, `dach`.
 */
// `konto`: znak konta jak przy szkicu zlecenia (`lib/rynek.kontoSzkicu`, uwagi przeglądu D12); null u gościa.
export const zapiszWyborDoboru = (dane, teraz = Date.now(), konto = null) => {
  try { window.localStorage.setItem(KLUCZ_WYBORU, JSON.stringify({ dane, czas: teraz, konto })) } catch { /* bez pamięci */ }
}
/** Cały zapis wyboru `{ dane, czas }` (czas do dopisku „zapisane DD.MM” w formularzu zlecenia, G4) albo null. */
export const odczytajZapisDoboru = (teraz = Date.now(), konto = null) => {
  try {
    const zapis = JSON.parse(window.localStorage.getItem(KLUCZ_WYBORU) || 'null')
    if (zapis?.dane && teraz - zapis.czas < WAZNOSC_WYBORU_MS) {
      return !zapis.konto || zapis.konto === konto ? { dane: zapis.dane, czas: zapis.czas } : null
    }
    if (zapis) window.localStorage.removeItem(KLUCZ_WYBORU)
  } catch { /* bez pamięci */ }
  return null
}
export const odczytajWyborDoboru = (teraz = Date.now(), konto = null) => odczytajZapisDoboru(teraz, konto)?.dane || null

/** „Zacznij od nowa” (J7): usuwa zapisany wybór doboru tego konta (albo gościa); cudzego nie rusza. */
export const wyczyscWyborDoboru = (konto = null) => {
  try {
    const zapis = JSON.parse(window.localStorage.getItem(KLUCZ_WYBORU) || 'null')
    if (zapis && (!zapis.konto || zapis.konto === konto)) window.localStorage.removeItem(KLUCZ_WYBORU)
  } catch { /* bez pamięci */ }
}

/** Powrót z doboru do formularza zlecenia (G4): „Zmień odpowiedzi” otwiera pytania z zachowanymi odpowiedziami. */
export const ADRES_ZMIANY_ODPOWIEDZI = '/dobierz-zestaw?powrot=1&do=zlecenie'

/** Dane wyboru do zapisu i zlecenia z odpowiedzi i wyliczeń. */
export const wyborDoboru = (o, { rok, kwp, pojemnosc } = {}) => {
  const cele = [...wybraneCele(o)]
  const opcja = opcjaZOdpowiedzi(o)
  const czesci = CZESCI_ROZBUDOWY.filter((c) => czesciRozbudowy(o).has(c))
  const bezPustych = (x) => Object.fromEntries(Object.entries(x).filter(([, v]) => v !== null && v !== undefined))
  return bezPustych({
    cele,
    kto_dobiera: opcja?.kto_dobiera ?? null,
    kto_kupuje: opcja?.kto_kupuje ?? null,
    sprzet: opcja?.sprzet ?? null,
    termin: cele.includes('szybki_montaz') ? 'szybko' : null,
    pme2: cele.includes('dotacja') || null,
    budzet_kwota: o?.budzet || null,
    zuzycie_kwh: rok || null,
    kwp: kwp || null,
    magazyn_kwh: pojemnosc || null,
    fazy: o?.fazy === '1' || o?.fazy === '3' ? Number(o.fazy) : null,
    dach: o?.montaz === 'grunt' ? 'Grunt' : null,
    typ: typZlecenia(o),
    // Rozbudowa (2026-10-26): zakres i to, co już jest, jako napisy opisu zlecenia (`opis` przyjmuje proste pola).
    rozbudowa: czesci.length ? opisRozbudowy(o, czesci) : null,
    obecna_instalacja: jestRozbudowa(o) ? obecnaInstalacja(o) : null,
  })
}

/** Typ zlecenia (`TypOgloszenia` w backendzie): rozbudowa, PV z magazynem albo samo PV. */
export const typZlecenia = (o) => (jestRozbudowa(o) ? 'rozbudowa' : wymagaMagazynu(o) ? 'pv_magazyn' : 'pv')

const liczbaPl = (v) => String(v).replace('.', ',')

/** Zakres rozbudowy dla wykonawcy: „moduły +4 kWp, falownik, magazyn energii” (dokładka tylko wybrana wprost). */
const opisRozbudowy = (o, czesci) => czesci
  .map((c) => (c === 'moduly' && wybranaDokladka(o) ? `${NAZWY_CZESCI[c]} +${liczbaPl(wybranaDokladka(o))} kWp` : NAZWY_CZESCI[c]))
  .join(', ')

/** To, co klient już ma, krótko dla wykonawcy: moc, falownik hybrydowy, magazyn. Null, gdy nic nie podał. */
export const obecnaInstalacja = (o) => {
  const czesci = [
    Number(o?.obecna_moc) > 0 ? `${liczbaPl(o.obecna_moc)} kWp` : null,
    o?.falownik_hybrydowy === 'tak' ? 'falownik hybrydowy' : null,
    o?.masz_magazyn === 'tak' ? `magazyn${o?.marka_magazynu && o.marka_magazynu !== 'inna' ? ` ${o.marka_magazynu}` : ''}` : null,
  ].filter(Boolean)
  return czesci.length ? czesci.join(', ') : null
}

/**
 * Szkic zlecenia dla „Wszystko po stronie wykonawcy” (bez zestawu): dzisiejszy formularz (`FormularzOgloszenia`)
 * czyta z niego `typ` i `sprzet`; pozostałe pola czekają na formularz z P3/P0 (kontrakt sekcja 4).
 */
export const szkicZlecenia = (o, wyliczenia) => {
  const w = wyborDoboru(o, wyliczenia)
  return { ...w, tryb: 'proste', sprzet: w.sprzet || 'z_firma' }
}

/**
 * Szkic zlecenia z drogi „Kilka pytań” (prosty dobór w trybie zlecenia, decyzja 2026-10-26) dla `onGotowe` i dla
 * „Wystaw zlecenie” na `/dobierz-zestaw`. Pola jak `szkicZlecenia` (cele, kto_dobiera, kto_kupuje, sprzet, termin,
 * pme2, budzet_kwota, zuzycie_kwh, kwp, magazyn_kwh, fazy, dach, typ, rozbudowa, obecna_instalacja) oraz:
 * - `zrodlo: 'odpowiedzi'` (formularz rozpoznaje po nim odpowiedzi z doboru, `FormularzOgloszenia.rodzajWejscia`);
 * - `wybor`: `wykonawca` (sprzęt w ofertach), `zestaw` (zestaw z propozycji) albo `wlasny` (sprzęt klienta);
 * - `opis`: pola opisu zlecenia jak `lib/rynek.ogloszenieZZestawu` (z zestawu) albo `lib/sprzetKlienta` (sprzęt
 *   klienta), z mocą, magazynem, dachem i rozbudową z odpowiedzi; `tryb: 'szczegolowe'`, gdy jest sprzęt;
 * - `odpowiedzi`: surowe odpowiedzi kreatora (do „Zmień odpowiedzi”).
 */
export const szkicDoboru = (o, wyliczenia, { wariant = null, sprzetKlienta = null } = {}) => {
  const w = szkicZlecenia(o, wyliczenia)
  const bezPustych = (x) => Object.fromEntries(Object.entries(x).filter(([, v]) => v !== null && v !== undefined && v !== ''))
  const zSprzetu = wariant ? ogloszenieZZestawu(wariant).opis : sprzetKlienta ? bezPustych(opisSprzetuKlienta(sprzetKlienta)) : {}
  const opis = bezPustych({
    kwp: w.kwp, magazyn_kwh: w.magazyn_kwh, dach: w.dach, fazy: w.fazy,
    rozbudowa: w.rozbudowa, obecna_instalacja: w.obecna_instalacja,
    ...zSprzetu,
  })
  const wybor = wariant ? 'zestaw' : sprzetKlienta ? 'wlasny' : 'wykonawca'
  // Wybrany zestaw rozstrzyga typ, moc i pojemność (np. magazyn z grupy „z magazynem” przy pominiętym pytaniu o magazyn).
  const zZestawu = wariant ? {
    typ: jestRozbudowa(o) ? w.typ : ogloszenieZZestawu(wariant).typ,
    kwp: opis.kwp ?? w.kwp, magazyn_kwh: opis.magazyn_kwh ?? null,
  } : {}
  return { ...w, ...zZestawu, zrodlo: 'odpowiedzi', wybor, tryb: wybor === 'wykonawca' ? 'proste' : 'szczegolowe', opis, odpowiedzi: o }
}

/**
 * Parametry estymatora zwykłych cen (`GET /api/v1/kits/zwykle-ceny`, kontrakt od koordynatora 2026-10-24).
 * `kto_kupuje=klient` daje widełki samego montażu; bez wyboru liczymy całość (`wykonawca`).
 */
export const parametryZwyklychCen = (o, { rok, kwp, pojemnosc }, zMagazynem) => {
  if (!rok && !kwp) return null
  // Rozbudowa: estymator zna moduły i magazyn, wymiany samego falownika nie wycenia, więc wtedy ceny nie pokazujemy.
  // Moc to sama dokładka (bez zużycia, z którego estymator policzyłby całą instalację od nowa).
  const czesci = czesciRozbudowy(o)
  if (jestRozbudowa(o) && !czesci.has('moduly') && !czesci.has('magazyn')) return null
  return {
    kwp: jestRozbudowa(o) ? kwp || 0 : kwp || null,
    zuzycie_kwh: jestRozbudowa(o) ? null : rok || null,
    magazyn_kwh: zMagazynem && pojemnosc ? pojemnosc : 0,
    kto_kupuje: opcjaZOdpowiedzi(o)?.kto_kupuje || 'wykonawca',
    dach: o?.montaz === 'grunt' ? 'Grunt' : null,
    fazy: o?.fazy === '1' || o?.fazy === '3' ? Number(o.fazy) : null,
  }
}
